import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Layers, Sparkles } from 'lucide-react';
import { getArchetypeById } from '../../config/produitArchetypes.js';
import { ARCHETYPE_IDS } from '../../config/produitArchetypes.js';
import {
  getOptionalSectionLabels,
  getRevealableHiddenSections,
  hasOptionalSections,
} from '../../utils/produitModalSections.js';

export default function ProduitArchetypeBanner({
  archetypeId,
  enrichExpanded,
  onToggleEnrich,
  revealedSections,
  onRevealSection,
  onRequestChangeArchetype,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedRef = useRef(null);

  useEffect(() => {
    if (!advancedOpen) return;
    const onDocClick = (e) => {
      if (advancedRef.current && !advancedRef.current.contains(e.target)) {
        setAdvancedOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [advancedOpen]);

  if (!archetypeId || archetypeId === ARCHETYPE_IDS.AVANCE) return null;

  const archetype = getArchetypeById(archetypeId);
  const hiddenSections = getRevealableHiddenSections(archetypeId).filter(
    (s) => !revealedSections.has(s.key)
  );
  const optionalLabels = getOptionalSectionLabels(archetypeId);
  const showEnrich = hasOptionalSections(archetypeId);

  return (
    <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <Layers className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Modèle : {archetype?.label ?? archetypeId}
            </p>
            {archetype?.subtitle ? (
              <p className="text-xs text-gray-500 truncate">{archetype.subtitle}</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showEnrich && (
            <button
              type="button"
              onClick={onToggleEnrich}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border ${
                enrichExpanded
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Enrichir la fiche
              {optionalLabels.length > 0 && !enrichExpanded ? (
                <span className="text-gray-400">({optionalLabels.length})</span>
              ) : null}
            </button>
          )}

          {hiddenSections.length > 0 && (
            <div className="relative" ref={advancedRef}>
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                Paramètres avancés
                <ChevronDown className={`h-3.5 w-3.5 transition ${advancedOpen ? 'rotate-180' : ''}`} />
              </button>
              {advancedOpen && (
                <div className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg py-1">
                  <p className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                    Afficher une section masquée
                  </p>
                  {hiddenSections.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        onRevealSection(key);
                        setAdvancedOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onRequestChangeArchetype}
            className="px-3 py-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
          >
            Changer
          </button>
        </div>
      </div>

      {showEnrich && enrichExpanded && optionalLabels.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">
          Sections complémentaires affichées : {optionalLabels.join(', ')}
        </p>
      )}
    </div>
  );
}
